import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SearchComponent from '../../../components/ui/SearchComponent';
import problemStatementService from '../../../services/problemStatementService';
import { useAuth } from '../../../contexts/AuthContext';
import ProblemStatementDetailsModal from './ProblemStatementDetailsModal';

const ProblemsTable = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sortField, setSortField] = useState('submissionDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProblems();
    }
  }, [user?.id]);

  const fetchProblems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await problemStatementService?.getUserProblemStatements(user?.id);
      setProblems(data);
    } catch (err) {
      console.error('Error fetching problems:', err);
      setError(err?.message || 'Failed to load problem statements');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-amber-50 text-amber-700 border-amber-200',
      'approved': 'bg-green-50 text-green-700 border-green-200',
      'rejected': 'bg-red-50 text-red-700 border-red-200'
    };
    return colors?.[status] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': 'Clock',
      'approved': 'CheckCircle',
      'rejected': 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };
    return labels?.[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-50 text-red-700 border-red-200',
      'medium': 'bg-orange-50 text-orange-700 border-orange-200',
      'low': 'bg-green-50 text-green-700 border-green-200'
    };
    return colors?.[priority] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'transportation': 'Car',
      'energy': 'Zap',
      'water': 'Droplet',
      'waste': 'Trash2',
      'housing': 'Home',
      'safety': 'Shield',
      'governance': 'Building2',
      'health': 'Heart',
      'education': 'GraduationCap',
      'economy': 'TrendingUp',
      'other': 'MoreHorizontal'
    };
    return icons?.[category] || 'Circle';
  };

  const getCategoryLabel = (problem) => {
    if (problem?.category === 'other' && problem?.customCategory) {
      return problem?.customCategory;
    }
    const labels = {
      'transportation': 'Transportation',
      'energy': 'Energy',
      'water': 'Water',
      'waste': 'Waste',
      'housing': 'Housing',
      'safety': 'Safety',
      'governance': 'Governance',
      'health': 'Healthcare',
      'education': 'Education',
      'economy': 'Economy',
      'other': 'Other'
    };
    return labels?.[problem?.category] || problem?.category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleProblemClick = (problemId) => {
    setSelectedProblemId(problemId);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedProblemId(null);
  };

  const filteredProblems = problems?.filter(problem =>
    problem?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    getCategoryLabel(problem)?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    problem?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const sortedProblems = [...filteredProblems]?.sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'title':
        aValue = a?.title?.toLowerCase();
        bValue = b?.title?.toLowerCase();
        break;
      case 'submissionDate':
        aValue = new Date(a?.createdAt);
        bValue = new Date(b?.createdAt);
        break;
      case 'status':
        aValue = a?.status;
        bValue = b?.status;
        break;
      case 'priority':
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityOrder?.[a?.priority] || 0;
        bValue = priorityOrder?.[b?.priority] || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Problem Statements</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Manage and track your submitted problems • {sortedProblems?.length} total problems
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <SearchComponent
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search problems..."
          className="w-full sm:w-96"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <Icon name="Loader" size={24} className="text-primary animate-spin" />
            <span className="text-sm sm:text-base text-muted-foreground">Loading problem statements...</span>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-red-900">Error Loading Problems</h4>
            <p className="text-xs sm:text-sm text-red-800 mt-1">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchProblems}
              className="mt-3"
              iconName="RefreshCw"
              iconPosition="left"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : sortedProblems?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 sm:p-12 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={24} className="sm:w-8 sm:h-8 text-muted-foreground" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No problems found</h3>
          <p className="text-sm sm:text-base text-gray-500 mb-6">
            {searchTerm
              ? "No problems match your search criteria. Try adjusting your search terms." :"You haven't submitted any problems yet. Get started by creating your first problem statement."
            }
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Problem</span>
                        {sortField === 'title' && (
                          <Icon
                            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                            size={14}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort('submissionDate')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Submitted</span>
                        {sortField === 'submissionDate' && (
                          <Icon
                            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                            size={14}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Status</span>
                        {sortField === 'status' && (
                          <Icon
                            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                            size={14}
                          />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                      Team
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Priority</span>
                        {sortField === 'priority' && (
                          <Icon
                            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                            size={14}
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedProblems?.map((problem) => (
                    <tr
                      key={problem?.id}
                      className="border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => handleProblemClick(problem?.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon
                              name={getCategoryIcon(problem?.category)}
                              size={20}
                              className="text-primary"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-foreground truncate">
                              {problem?.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {getCategoryLabel(problem)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-foreground">
                          {formatDate(problem?.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            problem?.status
                          )}`}
                        >
                          <Icon name={getStatusIcon(problem?.status)} size={12} />
                          <span>{getStatusLabel(problem?.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {problem?.assignedTeam ? (
                          <div className="text-sm text-foreground">
                            {problem?.assignedTeam?.team_name}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                            problem?.priority
                          )}`}
                        >
                          {problem?.priority?.charAt(0)?.toUpperCase() + problem?.priority?.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View - Visible on Mobile Only */}
          <div className="md:hidden space-y-3">
            {sortedProblems?.map((problem) => (
              <div
                key={problem?.id}
                className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleProblemClick(problem?.id)}
              >
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={getCategoryIcon(problem?.category)}
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground">
                      {problem?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getCategoryLabel(problem)}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Status:</span>
                    <span
                      className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                        problem?.status
                      )}`}
                    >
                      <Icon name={getStatusIcon(problem?.status)} size={12} />
                      <span>{getStatusLabel(problem?.status)}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Priority:</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                        problem?.priority
                      )}`}
                    >
                      {problem?.priority?.charAt(0)?.toUpperCase() + problem?.priority?.slice(1)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Submitted:</span>
                    <span className="text-xs text-foreground">{formatDate(problem?.createdAt)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Team:</span>
                    {problem?.assignedTeam ? (
                      <span className="text-xs text-foreground">{problem?.assignedTeam?.team_name}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">Not assigned</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <ProblemStatementDetailsModal
            isOpen={showDetailsModal}
            onClose={handleCloseModal}
            problemId={selectedProblemId}
          />
        </>
      )}
    </div>
  );
};

export default ProblemsTable;